<template>
    <div class="box directory-info">
        <h2 class="box-title">
            Directory Info
        </h2>
        <div class="box-content">
            <form
                id="directory-info"
                novalidate
                @submit.prevent="saveInfo"
            >
                <div class="settings-field categories">
                    <p class="title">
                        Apple Podcast Categories:
                    </p>
                    <div class="options">
                        <div
                            v-for="categoryIndex in [0, 1, 2]"
                            :key="categoryIndex"
                            class="settings-option"
                        >
                            <base-select
                                :options="showCategories"
                                placeholder="Choose category"
                                :container-class="['is-large']"
                                :initial-value="config.categories[categoryIndex]"
                                @change="value => $set(config.categories, categoryIndex, value)"
                            />
                        </div>
                    </div>
                </div>

                <div class="settings-field artist">
                    <p class="title">
                        Artist:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <input v-model="config.artist" type="text">
                        </div>
                    </div>
                </div>

                <div class="settings-field contact-email">
                    <p class="title">
                        Contact email:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <input v-model="config.contactEmail" type="email">
                        </div>
                    </div>
                </div>

                <div class="settings-field podcast-type">
                    <p class="title">
                        Podcast Type:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <base-select
                                :options="podcastTypes"
                                :initial-value="config.podcastType"
                                :container-class="['is-large']"
                                @change="value => config.podcastType = value"
                            />
                        </div>
                    </div>
                </div>

                <div class="settings-field language">
                    <p class="title">
                        Language:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <base-select
                                :options="languages"
                                :initial-value="config.language"
                                :container-class="['is-large']"
                                @change="value => config.language = value"
                            />
                        </div>
                    </div>
                </div>

                <div class="settings-field timezone">
                    <p class="title">
                        Time Zone:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <base-select
                                :options="timezones"
                                :initial-value="config.timezone"
                                :container-class="['is-large']"
                                @change="value => config.timezone = value"
                            />
                        </div>
                    </div>
                </div>

                <div class="settings-field keywords">
                    <p class="title">
                        Keywords:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <input v-model="config.keywords" type="text">
                            <p class="description">
                                Maximum of 12. Separate with commas.
                            </p>
                        </div>
                    </div>
                </div>

                <div class="settings-field support-link">
                    <p class="title">
                        Website:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <input
                                type="text"
                                :value="config.supportLink"
                                @blur="event => config.supportLink = prefixUrl(event.target.value)"
                            >
                            <p class="description">
                                Link to your donation. Patreon, or membership page.
                            </p>
                        </div>
                    </div>
                </div>

                <div class="settings-field copyright">
                    <p class="title">
                        Copyright:
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <input v-model="config.copyright" type="text">
                        </div>
                    </div>
                </div>

                <div class="settings-field explicit-content">
                    <p class="title">
                        Explicit Content?
                    </p>
                    <div class="options">
                        <div class="settings-option">
                            <base-checkbox
                                :values="config.explicitContent"
                                :value="config.explicitContent"
                                label="This podcast contains explicit material."
                                @change="value => config.explicitContent = value"
                            />
                        </div>
                    </div>
                </div>
                <div class="controll-container flex">
                    <base-form-button
                        class="btn"
                        :loading="formProcessing"
                    >
                        Save Changes
                    </base-form-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import BaseSelect from '@/components/BaseSelect';
import BaseCheckbox from '@/components/BaseCheckbox';
import BaseFormButton from '@/components/BaseFormButton';
import { isEqual, filter, toString } from 'lodash';
import { mapState } from 'vuex';
import { defaults as defaultOptions, podcastTypes, languages } from '@/utils/config/showOptions';
import prefixUrlInput from '@/utils/form/prefixUrlInput';
import toasts from '@/mixins/toasts';
import { getTimeZones } from '@vvo/tzdb';

export default {
    components: {
        BaseSelect,
        BaseCheckbox,
        BaseFormButton,
    },

    mixins: [
        toasts,
    ],

    props: {
        show: {
            type: Object,
            default: () => {},
        },
        formProcessing: {
            type: Boolean,
            required: true,
        },
        onChange: {
            type: Function,
            default: null,
        },
    },

    data: () => ({
        podcastTypes: podcastTypes,
        languages: languages,

        config: {
            categories: {},
            artist: '',
            contactEmail: '',
            podcastType: defaultOptions.podcastType,
            language: defaultOptions.language,
            timezone: defaultOptions.timezone,
            keywords: '',
            supportLink: '',
            copyright: '',
            explicitContent: false,
        },
    }),

    computed: {
        ...mapState(['user']),

        ...mapState({
            /**
             * Gets the list of categories.
             *
             * @param  {object}     state  Vuex state.
             * @returns {object[]}         List of show categories.
             */
            showCategories(state) {
                const categories = [];

                for (const value in state.showCategories) {
                    const label = state.showCategories[value];

                    categories.push({
                        value: toString(value),
                        label: label,
                    });
                }

                return categories;
            },
        }),

        /**
         * Gets the list of timezones.
         *
         * @returns {object[]}  List of timezones.
         */
        timezones() {
            return [
                {
                    value: 'GMT',
                    label: 'GMT',
                },
                ...getTimeZones().map(timezone => ({
                    value: timezone.name,
                    label: timezone.name.replace(/_/g, ' '),
                })),
            ];
        },

        /**
         * Gets the default options.
         *
         * @returns {object} Default options.
         */
        defaultOptions() {
            return defaultOptions;
        },

        /**
         * Gets list of categories.
         *
         * @returns {string[]} List of categories.
         */
        categoriesList() {
            return filter(Object.values(this.config.categories));
        },
    },

    watch: {
        config: {
            /**
             * Handles change of fields.
             *
             * @param  {object} data Updated data.
             */
            handler(data) {
                if (! this.validate() || this.formProcessing) {
                    return;
                }

                if (this.onChange) {
                    this.onChange({
                        ...data,
                        categories: this.categoriesList,
                    });
                }
            },
            deep: true,
        },

        show: {
            immediate: true,

            /**
             * Manages data.
             *
             * @param {object} show Show object data.
             * @param {*} oldShow Show object data.
             */
            handler(show, oldShow) {
                if (isEqual(show, oldShow)) {
                    return;
                }

                this.config.categories = show.categories || {};
                this.config.artist = show.artist || '';
                this.config.contactEmail = show.contactEmail || '';
                this.config.podcastType = show.podcastType || defaultOptions.podcastType;
                this.config.language = show.language || defaultOptions.language;
                this.config.timezone = show.timezone || defaultOptions.timezone;
                this.config.keywords = show.keywords || '';
                this.config.supportLink = show.supportLink || '';
                this.config.copyright = show.copyright || '';
                this.config.explicitContent = show.explicitContent || false;
            },
        },
    },

    methods: {
        /**
         * Submits form
         *
         * @returns {void} Do nothing, now.
         */
        saveInfo() {
            if (! this.validate() || this.formProcessing) {
                return;
            }

            this.$emit('submit');
        },

        /**
         * Valdiates form and set errors
         *
         * @returns {boolean} If validation is passed
         */
        validate() {
            const { language } = this.config;
            const hasCategories = !! this.categoriesList.length;

            if (language && hasCategories) {
                return true;
            }

            if (! language) {
                this.toastError({ message: { title: 'Please provide language.' } });
            }

            if (! hasCategories) {
                this.toastError({ message: { title: 'Please provide at least one category.' } });
            }

            return false;
        },

        /**
         * Prefixes given URL.
         *
         * @param    {string}  value  URL to prefix.
         * @returns  {string}         Prefixed URL.
         */
        prefixUrl(value) {
            return prefixUrlInput(value);
        },
    },
};
</script>
